/* eslint-disable max-lines */

import { PlusIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { CreatePackageForm } from "../components/esim/CreatePackageForm";
import { LocationZoneSelector } from "../components/esim/LocationZoneSelector";
import { PackageCard } from "../components/esim/PackageCard";
import { useDebounce } from "../hooks/useDebounce";
import { ICreatePackageModel, IEsimPackage, ILocationZone, IUpdatePackageModel } from "../interfaces/esim/EsimPackages.types";
import { createPackage, deletePackage, getLocationZones, getPackagesByLocationZone, updatePackage } from "../services/Esim.service";

const EMPTY_NEW_PACKAGE: Partial<IEsimPackage> = {
  totalGB: undefined,
  validityDays: undefined,
  cost: undefined,
  oldCost: undefined,
  mobileAppCost: undefined,
  journeyAddonCost: undefined,
  isRecommended: false,
  isUnlimited: false
};

// Fields typed as decimal in the UI: comma is accepted as the decimal separator.
const PRICE_FIELDS = ["cost", "oldCost", "mobileAppCost", "journeyAddonCost"];

// Mobile and add-on prices are optional overrides. An empty input means "no override"
// and must reach the API as null, never as 0 or as the previous value.
const toOptionalPrice = (value: number | string | null | undefined): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);

const EsimPackages: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [locationZones, setLocationZones] = useState<ILocationZone[]>([]);
  const [selectedZone, setSelectedZone] = useState<ILocationZone | null>(null);
  const [packages, setPackages] = useState<IEsimPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingPackage, setEditingPackage] = useState<IEsimPackage | null>(null);
  const [tempChanges, setTempChanges] = useState<Partial<IEsimPackage>>({});
  const [isInputFocused, setIsInputFocused] = useState(false);
  const debouncedSearch = useDebounce(search, 300);
  const [isCreating, setIsCreating] = useState(false);
  const [newPackage, setNewPackage] = useState<Partial<IEsimPackage>>(EMPTY_NEW_PACKAGE);

  useEffect(() => {
    const fetchLocationZones = async () => {
      const zoneSlug = searchParams.get("zone");
      if (!zoneSlug) {
        setLoading(true);
        try {
          const zonesResponse = await getLocationZones(debouncedSearch);
          if (zonesResponse.data.succeeded) {
            setLocationZones(zonesResponse.data.data);

            if (!selectedZone && zonesResponse.data.data.length > 0) {
              const firstZone = zonesResponse.data.data[0];
              setSelectedZone(firstZone);
              setSearchParams({ zone: firstZone.slug });
            }
          }
        } catch (error) {
          toast.error("Error fetching location zones");
        } finally {
          setLoading(false);
        }
      } else {
        try {
          const zonesResponse = await getLocationZones(debouncedSearch);
          if (zonesResponse.data.succeeded) {
            setLocationZones(zonesResponse.data.data);
          }
        } catch (error) {
          toast.error("Error fetching location zones");
        }
      }
    };

    void fetchLocationZones();
  }, [debouncedSearch]);

  useEffect(() => {
    const fetchPackages = async () => {
      const zoneSlug = searchParams.get("zone");
      if (!zoneSlug) return;

      setLoading(true);
      try {
        const packagesResponse = await getPackagesByLocationZone(zoneSlug);
        if (packagesResponse.data) {
          setPackages(packagesResponse.data);
        }
      } catch (error) {
        toast.error("Error fetching packages");
      } finally {
        setLoading(false);
      }
    };

    void fetchPackages();
  }, [searchParams]);

  useEffect(() => {
    const zoneSlug = searchParams.get("zone");
    if (zoneSlug && locationZones.length > 0) {
      const zone = locationZones.find((z) => z.slug === zoneSlug);
      if (zone) {
        setSelectedZone(zone);
      }
    }
  }, [searchParams]);

  const handlePackageUpdate = (packageId: number, field: string, value: string) => {
    if (field === "isRecommended" || field === "isUnlimited") {
      setTempChanges((prev) => ({
        ...prev,
        [field]: value === "true"
      }));
    } else if (PRICE_FIELDS.includes(field)) {
      const normalizedValue = value.replace(",", ".");
      setTempChanges((prev) => ({
        ...prev,
        [field]: normalizedValue === "" ? null : normalizedValue
      }));
    } else {
      const newValue = value === "" ? null : Number(value);
      if (!isNaN(newValue as number)) {
        setTempChanges((prev) => ({
          ...prev,
          [field]: newValue
        }));
      }
    }
  };

  const handleFinishEditing = async () => {
    if (!editingPackage) return;

    try {
      const updateData: IUpdatePackageModel = {
        id: editingPackage.id,
        dataBytes: tempChanges.totalGB !== null
          ? Math.round(Number(tempChanges.totalGB) * 1073741824)
          : Math.round(editingPackage.totalGB * 1073741824),
        validityDays: tempChanges.validityDays ?? editingPackage.validityDays,
        cost: tempChanges.cost !== null
          ? Number(tempChanges.cost)
          : editingPackage.cost,
        oldCost: tempChanges.oldCost !== null
          ? Number(tempChanges.oldCost)
          : editingPackage.oldCost,
        mobileAppCost: toOptionalPrice(tempChanges.mobileAppCost),
        journeyAddonCost: toOptionalPrice(tempChanges.journeyAddonCost),
        isRecommended: tempChanges.isRecommended ?? editingPackage.isRecommended,
        isUnlimited: tempChanges.isUnlimited ?? editingPackage.isUnlimited
      };

      const response = await updatePackage(updateData);

      if (response.status === 200) {
        setPackages((currentPackages) =>
          currentPackages.map((pkg) =>
            pkg.id === editingPackage.id
              ? {
                  ...pkg,
                  totalGB: Number(updateData.dataBytes) / 1073741824,
                  validityDays: updateData.validityDays,
                  cost: updateData.cost,
                  oldCost: updateData.oldCost,
                  mobileAppCost: updateData.mobileAppCost,
                  journeyAddonCost: updateData.journeyAddonCost,
                  isRecommended: updateData.isRecommended,
                  isUnlimited: updateData.isUnlimited
                }
              : pkg
          )
        );
        toast.success("Package updated successfully");
        setEditingPackage(null);
        setTempChanges({});
      } else {
        toast.error("Failed to update package");
      }
    } catch (error) {
      toast.error("Error updating package");
    }
  };

  const handleStartEditing = (pkg: IEsimPackage) => {
    setEditingPackage(pkg);
    setTempChanges({
      totalGB: pkg.totalGB,
      validityDays: pkg.validityDays,
      cost: pkg.cost,
      oldCost: pkg.oldCost,
      mobileAppCost: pkg.mobileAppCost,
      journeyAddonCost: pkg.journeyAddonCost,
      isRecommended: pkg.isRecommended,
      isUnlimited: pkg.isUnlimited
    });
  };

  // The pricing proposal in chat links here with ?package=<id>&price=<proposed>: open that package in edit
  // mode with the proposed price filled in, once per link. Nothing is saved until a person presses save.
  const appliedProposal = useRef<string | null>(null);
  useEffect(() => {
    const packageId = Number(searchParams.get("package"));
    const key = searchParams.toString();
    if (!packageId || appliedProposal.current === key) return;
    const pkg = packages.find((p) => p.id === packageId);
    if (!pkg) return;
    appliedProposal.current = key;
    handleStartEditing(pkg);
    const price = Number(searchParams.get("price"));
    if (price > 0) setTempChanges((prev) => ({ ...prev, cost: price }));
    document.getElementById(`package-${pkg.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [packages]);

  const handleZoneSelect = (zone: ILocationZone) => {
    setSearchParams({ zone: zone.slug });
    setIsInputFocused(false);
    setSearch("");
  };

  const handleZoneClear = () => {
    setSelectedZone(null);
    setSearch("");
    setIsInputFocused(false);
  };

  const handleCreatePackage = async () => {
    if (!selectedZone) {
      toast.error("Please select a location zone first");
      return;
    }

    try {
      const createData: ICreatePackageModel = {
        dataBytes: newPackage.totalGB
          ? Math.round(Number(newPackage.totalGB) * 1073741824)
          : 0,
        validityDays: newPackage.validityDays ?? 0,
        cost: newPackage.cost ? Number(newPackage.cost) : 0,
        oldCost: newPackage.oldCost ? Number(newPackage.oldCost) : 0,
        mobileAppCost: toOptionalPrice(newPackage.mobileAppCost),
        journeyAddonCost: toOptionalPrice(newPackage.journeyAddonCost),
        locationZoneId: selectedZone.id,
        isRecommended: newPackage.isRecommended ?? false,
        isUnlimited: newPackage.isUnlimited ?? false
      };

      const response = await createPackage(createData);

      if (response.status === 200) {
        const updatedPackages = await getPackagesByLocationZone(selectedZone.slug);
        if (updatedPackages.data) {
          setPackages(updatedPackages.data);
        }
        toast.success("Package created successfully");
        setIsCreating(false);
        setNewPackage(EMPTY_NEW_PACKAGE);
      } else {
        toast.error("Failed to create package");
      }
    } catch (error) {
      toast.error("Error creating package");
    }
  };

  const handleDeletePackage = async (id: number) => {
    try {
      const response = await deletePackage(id);
      if (response.status === 200) {
        setPackages((currentPackages) =>
          currentPackages.filter((pkg) => pkg.id !== id)
        );
        toast.success("Package deleted successfully");
      } else {
        toast.error("Failed to delete package");
      }
    } catch (error) {
      toast.error("Error deleting package");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">eSIM Packages Management</h1>
          <div className="w-full sm:w-auto">
            <LocationZoneSelector
              search={search}
              setSearch={setSearch}
              selectedZone={selectedZone}
              locationZones={locationZones}
              isInputFocused={isInputFocused}
              setIsInputFocused={setIsInputFocused}
              onZoneSelect={handleZoneSelect}
              onZoneClear={handleZoneClear}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              editingPackage={editingPackage}
              tempChanges={tempChanges}
              onStartEditing={handleStartEditing}
              onFinishEditing={handleFinishEditing}
              onDelete={handleDeletePackage}
              onPackageUpdate={handlePackageUpdate}
            />
          ))}

          <div
            className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg
              transition-all duration-300 transform hover:-translate-y-1 border-2 border-dashed
              border-gray-300
              hover:border-blue-500 cursor-pointer"
            onClick={() => !isCreating && setIsCreating(true)}
          >
            {isCreating ? (
              <CreatePackageForm
                newPackage={newPackage}
                setNewPackage={setNewPackage}
                onCreate={handleCreatePackage}
                onCancel={() => {
                  setIsCreating(false);
                  setNewPackage(EMPTY_NEW_PACKAGE);
                }}
              />
            ) : (
              <div className="p-6 h-full min-h-[300px] flex flex-col items-center justify-center gap-4">
                <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center
                  justify-center transition-colors duration-200 group-hover:bg-blue-100"
                >
                  <PlusIcon className="w-8 h-8 text-blue-500" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Add New Package
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Click to create a new eSIM package
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EsimPackages;
